import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Plus, MagnifyingGlass, ShoppingCart,
  Trash, PencilSimple
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import YearMonthNav from '../components/YearMonthNav';

import { API } from '../config/api';

const paymentStatusColors = {
  da_pagare: 'bg-red-100 text-red-700 border-red-200',
  parziale: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  pagato: 'bg-green-100 text-green-700 border-green-200',
};

export default function PurchasesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/purchases`);
      setList(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('Error fetching purchases:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm(t('purchase.deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/purchases/${id}`);
      toast.success(t('purchase.deleted'));
      fetchData();
    } catch (err) {
      toast.error(t('purchase.deleteError'));
    }
  };

  const filtered = (list || []).filter(d => {
    const matchSearch = d.purchase_number?.toLowerCase().includes(search.toLowerCase()) ||
      d.supplier_name?.toLowerCase().includes(search.toLowerCase()) ||
      d.supplier_invoice_number?.toLowerCase().includes(search.toLowerCase());
    if (!matchSearch) return false;
    const dateStr = d.date || '';
    if (dateStr.length < 7) return false;
    const year = parseInt(dateStr.substring(0, 4));
    if (year !== selectedYear) return false;
    if (selectedMonth !== null) {
      const month = parseInt(dateStr.substring(5, 7)) - 1;
      if (month !== selectedMonth) return false;
    }
    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(val || 0);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#0A0F1A] flex items-center gap-2" data-testid="purchases-title">
            <ShoppingCart size={28} weight="duotone" className="text-[#0033A0]" />
            {t('purchase.title')}
          </h1>
          <p className="text-[#4A5568] mt-1">{t('purchase.subtitle')}</p>
        </div>
        <Button
          onClick={() => navigate('/acquisti/new')}
          className="bg-[#0033A0] hover:bg-[#0047AB] text-white shrink-0"
          data-testid="new-purchase-button"
        >
          <Plus size={20} className="mr-2" />
          {t('purchase.newPurchase')}
        </Button>
      </div>

      <div className="relative max-w-md">
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" size={18} />
        <Input
          placeholder={t('purchase.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 border-[#E2E8F0]"
          data-testid="purchase-search"
        />
      </div>

      <YearMonthNav
        items={list}
        dateField="date"
        totalField="total"
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        selectedMonth={selectedMonth}
        setSelectedMonth={setSelectedMonth}
      />

      {loading ? (
        <div className="text-center py-12 text-[#4A5568]">{t('common.loading')}</div>
      ) : filtered.length === 0 ? (
        <Card className="border-[#E2E8F0]">
          <CardContent className="text-center py-16">
            <ShoppingCart size={48} className="mx-auto text-[#CBD5E0] mb-4" weight="duotone" />
            <h3 className="font-medium text-[#0A0F1A] mb-2">{t('purchase.noPurchases')}</h3>
            <p className="text-sm text-[#4A5568] mb-4">{t('purchase.createFirst')}</p>
            <Button
              onClick={() => navigate('/acquisti/new')}
              className="bg-[#0033A0] hover:bg-[#0047AB] text-white"
              data-testid="create-first-purchase"
            >
              <Plus size={20} className="mr-2" />
              {t('purchase.newPurchase')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((item) => (
            <Card key={item.id} className="border-[#E2E8F0] hover:border-[#CBD5E0] transition-colors" data-testid={`purchase-card-${item.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <span className="font-outfit font-bold text-[#0033A0]">{item.purchase_number}</span>
                      <Badge className={`text-[10px] ${paymentStatusColors[item.payment_status] || 'bg-gray-100 text-gray-600'}`}>
                        {t(`purchase.paymentLabels.${item.payment_status}`) || item.payment_status}
                      </Badge>
                      {item.category && (
                        <span className="text-[10px] text-[#4A5568] bg-[#F4F6F8] px-2 py-0.5 rounded">
                          {t(`purchase.categories.${item.category}`) || item.category}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-[#0A0F1A] font-medium">{item.supplier_name}</p>
                    <p className="text-xs text-[#4A5568]">
                      {formatDate(item.date)} | {item.supplier_invoice_number ? `Fatt. ${item.supplier_invoice_number}` : ''} | {formatCurrency(item.total)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => navigate(`/acquisti/${item.id}/edit`)} data-testid={`purchase-edit-${item.id}`}>
                      <PencilSimple size={18} className="text-[#4A5568]" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)} data-testid={`purchase-delete-${item.id}`}>
                      <Trash size={18} className="text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

